import type { AssignmentDetail } from '@servicetim/shared';
import {
  useDeleteAssignment,
  useProjectAssignments,
  useProjectRoles,
  useTenantRoles,
} from '@servicetim/ui/api';
import {
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { hasTenantRole, useSessionClaims } from '@servicetim/ui/lib';
import { Plus, Trash2, UserRound, UsersRound } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Section } from '../ui/section';
import { AssignmentDialog } from './assignment-dialog';

/** Every role assignment of the project (user or group grantees) with add and remove. */
export function AssignmentsTable({ projectId }: { projectId: string }) {
  const assignments = useProjectAssignments(projectId);
  const roles = useProjectRoles(projectId);
  const { tenantRole } = useSessionClaims();
  const tenantRoles = useTenantRoles();
  const remove = useDeleteAssignment(projectId);
  const confirm = useConfirm();
  const [adding, setAdding] = useState(false);
  const isAdmin = hasTenantRole(tenantRole, 'admin');

  const assignableRoles = useMemo(
    () => [...(roles.data ?? []), ...(isAdmin && tenantRoles.data ? tenantRoles.data : [])],
    [roles.data, tenantRoles.data, isAdmin],
  );

  const onRemove = async (a: AssignmentDetail) => {
    const who = a.user?.displayName ?? a.group?.name ?? 'this grantee';
    const ok = await confirm({
      title: `Remove ${a.role.name} from ${who}?`,
      confirmLabel: 'Remove',
      destructive: true,
      onConfirm: () => remove.mutateAsync({ roleId: a.roleId, assignmentId: a.id }),
    });
    if (ok) toast.success('Assignment removed');
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: handlers and mutation resets are stable for the component lifetime
  const columns = useMemo<ColumnDef<AssignmentDetail>[]>(
    () => [
      {
        id: 'grantee',
        header: 'Grantee',
        accessorFn: (a) => a.user?.displayName ?? a.group?.name ?? '',
        cell: ({ row }) => {
          const a = row.original;
          if (a.user) {
            return (
              <span className="inline-flex items-center gap-2">
                <UserRound className="size-4 text-fg-subtle" aria-hidden="true" />
                <span>
                  <span className="font-medium text-fg">{a.user.displayName}</span>
                  <span className="block text-fg-muted text-xs">{a.user.email}</span>
                </span>
              </span>
            );
          }
          if (a.group) {
            return (
              <span className="inline-flex items-center gap-2">
                <UsersRound className="size-4 text-fg-subtle" aria-hidden="true" />
                <span className="font-medium text-fg">{a.group.name}</span>
                <Badge variant="secondary">Group</Badge>
              </span>
            );
          }
          return <span className="text-fg-muted">Unknown</span>;
        },
      },
      {
        id: 'role',
        header: 'Role',
        accessorFn: (a) => a.role.name,
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-2">
            {row.original.role.name}
            <code className="font-mono text-fg-muted text-xs">{row.original.role.key}</code>
          </span>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Since',
        size: 140,
        cell: ({ getValue }) => new Date(getValue<string>()).toLocaleDateString(),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 60,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Remove assignment"
              onClick={() => void onRemove(row.original)}
            >
              <Trash2 />
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <Section
      title="Assignments"
      description="Who holds which role in this project."
      actions={
        <Button size="sm" onClick={() => setAdding(true)} disabled={assignableRoles.length === 0}>
          <Plus />
          Assign role
        </Button>
      }
    >
      <DataTable
        columns={columns}
        data={assignments.data}
        getRowId={(a) => a.id}
        isLoading={assignments.isPending}
        error={assignments.error}
        onRetry={() => void assignments.refetch()}
        dense
        caption="Role assignments"
        emptyTitle="No assignments"
        emptyDescription="Tenant admins hold every permission implicitly; everyone else needs a role here."
      />
      <AssignmentDialog
        projectId={projectId}
        roles={assignableRoles}
        open={adding}
        onOpenChange={setAdding}
      />
    </Section>
  );
}
